import { computed, readonly, ref } from 'vue'
import { type ShiftDTO, ShiftTypeDTO } from '@/generated'
import { addDays, addMinutes, formatISO, isAfter, isBefore, isToday, startOfDay } from 'date-fns'
import useTimeOptions from '@/composables/useTimeOptions'
import { ShiftService } from '@/services/telehealthApi/ShiftService'
import { UserSettingsService } from '@/services/telehealthApi/UserSettingsService'
import { NoticeType } from '@/stores/models/Notice'
import { useUserStore } from '@/stores/user'
import { storeToRefs } from 'pinia'
import { useNoticeStore } from '@/stores/notice'
import { useI18n } from 'vue-i18n'
import { useConsultationRequestStore } from '@/stores/consultationRequest'

function useShiftForm() {
  const noticeStore = useNoticeStore()
  const userStore = useUserStore()
  const { t } = useI18n()
  const { user } = storeToRefs(userStore)
  const { refreshIncomingConsultationRequestList } = useConsultationRequestStore()

  const selectedShift = ref<ShiftTypeDTO | undefined>(user.value?.currentShift)
  const selectedDate = ref<Date>(new Date())

  const { timeOptions } = useTimeOptions(selectedDate, 30)
  const selectedTime = ref<number | null>(timeOptions.value[0]?.value ?? null)

  const saveAttempt = ref<boolean>(false)

  const isTwoOrMoreDaysAfter = computed(() => {
    return isAfter(selectedDate.value, addDays(new Date(), 2))
  })

  const isDateTimeInThePast = computed<boolean>(() => {
    if (selectedTime.value) {
      const selectedDateTime = addMinutes(startOfDay(selectedDate.value), selectedTime.value)

      return isBefore(selectedDateTime, new Date())
    }

    return false
  })

  const errorMessage = computed<string | null>(() => {
    if (saveAttempt.value) {
      if (!selectedShift.value) return t('Please select a shift to continue')
      if (!selectedTime.value && selectedShift.value !== ShiftTypeDTO.NO_SHIFT)
        return t('Please, select valid time')
    }

    if (isDateTimeInThePast.value) {
      return `${t(
        'The shift end must not be in the past. Please choose a time between now and the day after tomorrow'
      )}.`
    } else if (isTwoOrMoreDaysAfter.value) {
      return `${t(
        'The shift end must not be later than two days from today. Please choose a time between now and the day after tomorrow'
      )}.`
    }

    return null
  })

  function updateShift(shift: ShiftTypeDTO) {
    if (shift === ShiftTypeDTO.NO_SHIFT) {
      resetState()
    }

    saveAttempt.value = false
    selectedShift.value = shift
  }

  function updateSelectedTime(minutes: number) {
    saveAttempt.value = false
    selectedTime.value = minutes
  }

  function updateSelectedDate(date: Date) {
    saveAttempt.value = false
    selectedDate.value = date
  }

  function onDateSelection(date: Date) {
    updateSelectedDate(date)

    if (isToday(date)) {
      updateSelectedTime(timeOptions.value[0].value)
    }
  }

  function resetState() {
    saveAttempt.value = false
    selectedDate.value = new Date()
    selectedTime.value = timeOptions.value[0]?.value ?? null
  }

  async function onSubmit(showSuccessMessage = false) {
    saveAttempt.value = true

    if (errorMessage.value) {
      return
    }

    try {
      const shiftDTO: ShiftDTO = {
        userName: user.value!.userName,
        type: selectedShift.value as ShiftTypeDTO
      }

      if (
        (selectedShift.value === ShiftTypeDTO.FIRST_SHIFT ||
          selectedShift.value === ShiftTypeDTO.BACKUP_SHIFT) &&
        selectedTime.value &&
        selectedTime.value > 0
      ) {
        const endDate = addMinutes(startOfDay(selectedDate.value), selectedTime.value)
        shiftDTO.end = formatISO(endDate)
      }

      await ShiftService.createAndBeginShift(shiftDTO)
      userStore.setCurrentShift(selectedShift.value)
      userStore.setHadSelectedShift(true)
      userStore.setEndOfShiftDate(shiftDTO.end)

      await UserSettingsService.syncUserWithBackend()

      if (selectedShift.value === ShiftTypeDTO.NO_SHIFT) {
        await refreshIncomingConsultationRequestList()
      }

      if (showSuccessMessage) {
        noticeStore.add({
          type: NoticeType.SUCCESS,
          message: t('You changed your shift')
        })
      }
    } catch (error) {
      console.error(error)
      noticeStore.add({
        type: NoticeType.ERROR,
        message: t('Failed to save data')
      })
    }
  }

  return {
    selectedShift: readonly(selectedShift),
    selectedDate: readonly(selectedDate),
    selectedTime: readonly(selectedTime),
    isDateTimeInThePast,
    isTwoOrMoreDaysAfter,
    errorMessage,
    timeOptions,
    updateShift,
    updateSelectedTime,
    onDateSelection,
    onSubmit
  }
}

export default useShiftForm
